import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { useCallback } from "react";

import { UpdatePurchasingPriorityMutation } from "../job-tracking/queries/tracking-data/useUpdatePurchasingPriorityMutation";
import { BomNode } from "../job-tracking/queries/tracking-data/bom-hierarchy-generator";

export interface RecursiveDeletePurchasingPrioritiesButtonProps {
  bomNode: BomNode;
  updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation;
  className: string;
}

/**
 * Component that renders a button to delete the job-specific purchasing priority for a BOM node, and all its descendants.
 */
export const RecursiveDeletePurchasingPrioritiesButton: React.FC<RecursiveDeletePurchasingPrioritiesButtonProps> = ({
  bomNode,
  updatePurchasingPriorityMutation,
  className,
}) => {
  // Define a click handler for the delete button
  const handleDelete = useCallback(() => {
    // Do nothing if this node doesn't have a job-specific record to delete
    if (!bomNode.purchasingPriority.nodeJobRecord) return;

    // Confirm with the user before deleting anything
    const confirmResult = confirm(
      "This will delete the job-specific priorities for this item and all it's children. They will be reverted to the " +
        "template or default priorities.\n\nThis action cannot be undone. Are you sure you want to continue?",
    );
    if (!confirmResult) return;

    // Apply the mutation to delete the record
    const recordsToDelete = [bomNode, ...getAllJobSpecificNodes(bomNode.children)].map(
      (node) => node.purchasingPriority.nodeJobRecord!,
    );
    updatePurchasingPriorityMutation.mutate({ action: "delete", recordsToDelete });
  }, [bomNode, updatePurchasingPriorityMutation]);

  // Render nothing unless this is a top-level node with a job-specific record associated
  if (bomNode.recursiveDepth !== 0 || !bomNode.purchasingPriority.nodeJobRecord) return null;

  return (
    <OverlayTrigger
      overlay={
        <Tooltip>
          Revert to template
          <br />
          (discard all job-specific priorities)
        </Tooltip>
      }
    >
      <Button
        variant="link"
        size="sm"
        disabled={updatePurchasingPriorityMutation.isPending}
        onClick={handleDelete}
        className={className}
      >
        <Icon icon="trash3" className="text-secondary" />
      </Button>
    </OverlayTrigger>
  );
};

/** Recursively get all bom nodes that have a job-specific priority record associated with them. */
function getAllJobSpecificNodes(bomNodes: BomNode[]): BomNode[] {
  return bomNodes
    .filter((node) => !!node.purchasingPriority.nodeJobRecord)
    .concat(bomNodes.flatMap((node) => getAllJobSpecificNodes(node.children)));
}
